import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Collapse } from '@mui/material'
import type { ProjectNodeProps } from './types/SessionTreeProps'
import {
  createNodeHeaderSx,
  createToggleSx,
  createNodeIconSx,
  createCountSx,
  createWrapperSx,
  childrenContainerSx,
  labelSx,
  actionsSx,
  DEFAULT_EXPANDED_ICON,
  DEFAULT_COLLAPSED_ICON,
  DEFAULT_PROJECT_FONT_SIZE,
  DEFAULT_PROJECT_FONT_WEIGHT,
  DEFAULT_VERTICAL_GAP,
  DEFAULT_TOGGLE_WIDTH,
  nodeRowHoverRevealSx,
} from './styles/sessionTreeStyles'

export const ProjectNode: FC<ProjectNodeProps> = ({
  label,
  icon,
  styleConfig,
  count,
  actions,
  stats,
  isExpanded,
  onToggle,
  expandPosition,
  children,
}) => {
  const toggleIcon = isExpanded
    ? (styleConfig?.toggleIcon?.expanded ?? DEFAULT_EXPANDED_ICON)
    : (styleConfig?.toggleIcon?.collapsed ?? DEFAULT_COLLAPSED_ICON)

  const showCount = styleConfig?.showCount !== false && count !== undefined
  const verticalGap = styleConfig?.verticalGap ?? DEFAULT_VERTICAL_GAP
  const wrapperSx = createWrapperSx(styleConfig?.wrapper)

  const nodeContent = (
    <>
      <Box sx={nodeRowSx}>
        {!wrapperSx && <Box sx={level1IndentSpacerSx} />}
        <Box
          sx={createNodeHeaderSx(
            styleConfig,
            DEFAULT_PROJECT_FONT_SIZE,
            DEFAULT_PROJECT_FONT_WEIGHT,
          )}
          onClick={onToggle}
        >
          {expandPosition === 'left' && (
            <Box className="node-toggle" sx={createToggleSx(styleConfig)}>
              {toggleIcon}
            </Box>
          )}

          {icon !== null && icon !== undefined && (
            <Box sx={createNodeIconSx(styleConfig)}>{icon}</Box>
          )}

          <Box sx={labelSx}>
            {label}
            {showCount && (
              <Box component="span" sx={createCountSx(styleConfig)}>
                ({count})
              </Box>
            )}
          </Box>

          {stats}

          {actions && actions.length > 0 && (
            <Box className="actions-container" sx={actionsSx}>
              {actions}
            </Box>
          )}

          {expandPosition === 'right' && (
            <Box className="node-toggle" sx={createToggleSx(styleConfig)}>
              {toggleIcon}
            </Box>
          )}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={childrenContainerSx}>{children}</Box>
      </Collapse>
    </>
  )

  if (wrapperSx) {
    return <Box sx={createNodeContainerWithWrapperSx(wrapperSx, verticalGap)}>{nodeContent}</Box>
  }

  return <Box sx={createNodeContainerSx(verticalGap)}>{nodeContent}</Box>
}

const nodeRowSx: SxProps<Theme> = [{ display: 'flex', alignItems: 'center' }, nodeRowHoverRevealSx]

const level1IndentSpacerSx: SxProps<Theme> = {
  width: DEFAULT_TOGGLE_WIDTH,
  minWidth: DEFAULT_TOGGLE_WIDTH,
}

function createNodeContainerSx(verticalGap: number): SxProps<Theme> {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: verticalGap,
    mb: verticalGap,
  }
}

function createNodeContainerWithWrapperSx(
  wrapperSx: SxProps<Theme>,
  verticalGap: number,
): SxProps<Theme> {
  return {
    ...(wrapperSx as object),
    display: 'flex',
    flexDirection: 'column',
    gap: verticalGap,
  }
}
